import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Mic } from "lucide-react";
import api, { apiErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import Logo from "@/components/Logo";
import { Button } from "@/components/ui/button";

export default function Onboarding() {
  const { user, refresh } = useAuth();
  const nav = useNavigate();
  const [step, setStep] = useState(0);
  const [meta, setMeta] = useState({ languages: [], genres: [], countries: [] });
  const [langs, setLangs] = useState([]);
  const [genres, setGenres] = useState([]);
  const [country, setCountry] = useState("");

  useEffect(() => { api.get("/meta").then(({ data }) => setMeta(data)); }, []);

  const toggle = (arr, set, v) => set(arr.includes(v) ? arr.filter((x) => x !== v) : [...arr, v]);

  const finish = async () => {
    try {
      await api.post("/me/onboard", { favorite_languages: langs, favorite_genres: genres, country });
      await refresh();
      toast.success("You're all set! Ready to sing?");
      nav("/", { replace: true });
    } catch (e) { toast.error(apiErr(e)); }
  };

  const Chip = ({ active, ...p }) => (
    <button {...p} className={`px-4 py-2 rounded-full text-sm font-medium border transition-colors ${active ? "bg-neon-pink border-neon-pink text-white" : "border-white/15 text-muted-foreground hover:border-white/40 hover:text-white"}`} />
  );

  const steps = [
    {
      title: `Welcome, ${user?.display_name}!`, sub: "Let's personalize your experience in a few quick steps.",
      body: <div className="flex justify-center py-6"><Logo size={100} /></div>,
    },
    {
      title: "Pick your favorite languages", sub: "We'll surface songs you'll love.",
      body: <div className="flex flex-wrap gap-2 justify-center max-h-64 overflow-y-auto no-scrollbar">{meta.languages.map((l) => <Chip key={l} active={langs.includes(l)} onClick={() => toggle(langs, setLangs, l)} data-testid={`onb-lang-${l}`}>{l}</Chip>)}</div>,
    },
    {
      title: "Choose your genres", sub: "Tell us what moves you.",
      body: <div className="flex flex-wrap gap-2 justify-center">{meta.genres.map((g) => <Chip key={g} active={genres.includes(g)} onClick={() => toggle(genres, setGenres, g)} data-testid={`onb-genre-${g}`}>{g}</Chip>)}</div>,
    },
    {
      title: "Where are you from?", sub: "Optional — helps with regional discovery.",
      body: <div className="flex flex-wrap gap-2 justify-center max-h-64 overflow-y-auto no-scrollbar">{meta.countries.map((c) => <Chip key={c} active={country === c} onClick={() => setCountry(c)}>{c}</Chip>)}</div>,
    },
    {
      title: "You get 2 free recordings", sub: "Record your first two performances free. Go Premium anytime for unlimited singing.",
      body: <div className="flex justify-center py-4"><div className="w-24 h-24 rounded-full bg-neon-pink/20 flex items-center justify-center glow-pink"><Mic className="w-12 h-12 text-neon-pink" /></div></div>,
    },
    {
      title: "Ready to sing?", sub: "Your stage is waiting.",
      body: <div className="text-center py-6"><p className="text-6xl">🎤</p></div>,
    },
  ];
  const s = steps[step];

  return (
    <div className="min-h-screen bg-mesh flex flex-col items-center justify-center px-4 py-10">
      <div className="w-full max-w-lg glass rounded-3xl p-8 fade-up">
        <div className="flex gap-1.5 mb-8">
          {steps.map((_, i) => <div key={i} className={`h-1.5 flex-1 rounded-full transition-colors ${i <= step ? "bg-neon-pink" : "bg-white/10"}`} />)}
        </div>
        <h1 className="font-head text-2xl sm:text-3xl font-extrabold text-center tracking-tight">{s.title}</h1>
        <p className="text-sm text-muted-foreground text-center mt-2 mb-6">{s.sub}</p>
        <div className="mb-8">{s.body}</div>
        <div className="flex gap-3">
          {step > 0 && <Button variant="ghost" onClick={() => setStep(step - 1)} className="flex-1 rounded-full">Back</Button>}
          {step < steps.length - 1
            ? <Button onClick={() => setStep(step + 1)} data-testid="onb-next" className="flex-1 rounded-full bg-neon-pink hover:bg-neon-pink/90 font-bold">Continue</Button>
            : <Button onClick={finish} data-testid="onb-finish" className="flex-1 rounded-full bg-neon-pink hover:bg-neon-pink/90 glow-pink font-bold">Start Singing</Button>}
        </div>
        <button onClick={finish} className="w-full text-center text-xs text-muted-foreground mt-4 hover:text-white">Skip for now</button>
      </div>
    </div>
  );
}
